"use client";
import { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/Select";

import { Badge } from "../components/ui/Badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/Avatar";
// Removed Table imports
import { customerService } from "../services/customers";
import {
  Search,
  Plus,
  Mail,
  Phone,
  Edit,
  ShoppingBag,
  Package,
  DollarSign,
  XCircle,
  Eye,
  Loader2
} from "lucide-react";
import { EditarClienteDialog } from "../components/EditarClienteDialog";
import { toast } from "sonner";

type ClientType = "cliente" | "fornecedor";

interface Client {
  id: number;
  name: string;
  email: string;
  phone: string;
  city: string;
  type: ClientType;
  status: string;
  purchasedProducts?: number;
  supplierCode?: string;
  productsOnSite?: number;
  productsSold?: number;
  productsPaid?: number;
  productsReturned?: number;
}

interface ClientesPageProps {
  onNavigate?: (page: string) => void;
}

export function ClientesPage({ onNavigate }: ClientesPageProps = {}) {
  const navigate = useNavigate();
  const { user } = useOutletContext<any>() || { user: null };
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);


  const handleEditClick = (client: Client) => {
    setSelectedClient(client);
    setEditDialogOpen(true);
  };

  const handleViewProducts = (client: Client) => {
    if (onNavigate) {
      onNavigate(`/clientes/${client.id}?tab=compras`);
    } else {
      navigate(`/clientes/${client.id}?tab=compras`);
    }
  };


  const openAddPopup = () => {
    setSelectedClient(null);
    setEditDialogOpen(true);
  };



  const [clients, setClients] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterType, setFilterType] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const [stats, setStats] = useState({ total: 0, clientes: 0, fornecedores: 0 });
  const [loading, setLoading] = useState(true);

  const fetchClientes = async (page = 1) => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = { page, per_page: 15 };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filterType !== 'all') params.type = filterType;
      if (filterStatus !== 'all') params.status = filterStatus;

      const response = await customerService.getAll(params);
      const { data, stats: responseStats, meta } = response.data;
      setClients(data);
      if (responseStats) setStats(responseStats);
      if (meta) {
        setPagination({
          current_page: meta.current_page,
          last_page: meta.last_page,
          total: meta.total
        });
      }
    } catch (error) {
      console.error("Erro ao buscar clientes:", error);
      toast.error("Erro ao carregar lista de clientes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientes(1);
  }, [debouncedSearch, filterType, filterStatus]);

  const getTypeLabel = (type: ClientType) => {
    switch (type) {
      case 'cliente': return 'Cliente';
      case 'fornecedor': return 'Fornecedor';
      default: return type;
    }
  };

  const getTypeBadgeColor = (type: ClientType) => {
    switch (type) {
      case 'cliente': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'fornecedor': return 'bg-purple-100 text-purple-700 border-purple-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusBadge = (status: string) => {
    if (!status) return null;

    let colorClass = '';
    let tooltipText = '';

    switch (status) {
      case 'VIP':
        colorClass = 'bg-[#D4C070] text-white border-[#D4C070]'; // Dourado
        tooltipText = 'Mais de 10 compras no total (ativo)';
        break;
      case 'Constante':
        colorClass = 'bg-[#849D70] text-white border-[#849D70]'; // Verde
        tooltipText = 'Entre 6 e 10 compras no total (ativo)';
        break;
      default:
        colorClass = 'bg-red-50 text-red-600 border-red-200'; // Vermelho sutil
        tooltipText = 'Nenhuma compra realizada ou inativo há mais de 1 ano';
        break;
    }

    return (
      <div title={tooltipText} className="cursor-help inline-block">
        <Badge variant="outline" className={colorClass}>
          {status}
        </Badge>
      </div>
    );
  };

  return (
    <main className="flex-1 p-6 bg-gray-50">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#68686A]">Clientes</h1>
        <p className="text-[#68686A]">Gerencie sua base de clientes e fornecedores</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-200">
          <CardHeader>
            <CardTitle className="text-[#68686A]">Total Geral</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
            <p className="text-sm text-[#849D70]">Todos os usuários</p>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-200">
          <CardHeader>
            <CardTitle className="text-[#68686A]">Clientes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-gray-900">{stats.clientes}</p>
            <p className="text-sm text-blue-600">Apenas compram</p>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-200">
          <CardHeader>
            <CardTitle className="text-[#68686A]">Fornecedores</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-gray-900">{stats.fornecedores}</p>
            <p className="text-sm text-purple-600">Compram e vendem</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Buscar por nome, email, código (para CPF/CNPJ digite apenas números)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-gray-300 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent"
          />
        </div>
        <div className="w-full sm:w-48">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="border-gray-300 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              <SelectItem value="cliente">Apenas Clientes</SelectItem>
              <SelectItem value="fornecedor">Apenas Fornecedores</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-48">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="border-gray-300 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="vip">VIP</SelectItem>
              <SelectItem value="fiel">Constante</SelectItem>
              <SelectItem value="inativo">Pouca ou Nenhuma Compra</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={openAddPopup}
          className="bg-[#D4C070] hover:bg-[#849D70] text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Novo Cliente
        </Button>
      </div>

      <Card className="border border-gray-200 relative min-h-[200px]">
        {loading && (
          <div className="absolute inset-0 z-10 bg-white/50 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#D4C070]" />
          </div>
        )}
        <CardContent className="p-0">
          <div className="divide-y divide-gray-200">
            {clients.length === 0 ? (
              <div className="p-6 text-center text-gray-500">Nenhum cliente encontrado</div>
            ) : clients.map((client) => (
              <div key={client.id} className="flex flex-col md:flex-row md:items-center p-6 hover:bg-gray-50 gap-4 md:gap-6">

                {/* Avatar e Identificação */}
                <div className="flex items-start md:items-center gap-3 md:w-1/4">
                  <Avatar className="h-10 w-10 flex-shrink-0">
                    <AvatarImage src={`/placeholder-avatar-${client.id}.jpg`} />
                    <AvatarFallback className="bg-[#D4C070] text-white">
                      {client.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-1.5 min-w-0">
                    <span className="font-medium text-gray-900 truncate">{client.name}</span>
                    <div className="flex gap-2 flex-wrap">
                      <Badge
                        variant="outline"
                        className={getTypeBadgeColor(client.type)}
                      >
                        {getTypeLabel(client.type)}
                      </Badge>
                      {getStatusBadge(client.status)}
                    </div>
                  </div>
                </div>

                {/* Contato */}
                <div className="flex flex-col gap-2 md:w-1/4">
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    <Mail className="h-4 w-4 text-[#68686A] flex-shrink-0" />
                    <span className="text-[#68686A] truncate" title={client.email}>{client.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-[#68686A] flex-shrink-0" />
                    <span className="text-[#68686A] whitespace-nowrap">{client.phone}</span>
                  </div>
                </div>

                {/* Informações de Compra/Fornecimento */}
                <div className="flex-1 min-w-0">
                  {client.type === 'cliente' ? (
                    <div className="flex items-center gap-1 px-2 py-1 bg-purple-50 rounded text-xs cursor-help w-fit" title="Número de pedidos (compras) realizados pelo cliente">
                      <ShoppingBag className="h-3 w-3 text-purple-600 flex-shrink-0" />
                      <span className="text-[#68686A]">Compras:</span>
                      <span className="font-medium text-gray-900">{client.purchasedProducts || 0}</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-sm">
                        <span className="text-[#68686A]">Cód: </span>
                        <span className="font-medium text-gray-900">{client.supplierCode}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <div className="flex items-center gap-1 px-2 py-1 bg-purple-50 rounded text-xs cursor-help" title="Número de pedidos (compras) realizados pelo fornecedor">
                          <ShoppingBag className="h-3 w-3 text-purple-600 flex-shrink-0" />
                          <span className="text-[#68686A]">Compras:</span>
                          <span className="text-gray-900 font-medium">{client.purchasedProducts || 0}</span>
                        </div>
                        <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded text-xs cursor-help" title="Peças atualmente publicadas à venda no site">
                          <Package className="h-3 w-3 text-blue-600 flex-shrink-0" />
                          <span className="text-[#68686A]">Publicados:</span>
                          <span className="text-gray-900 font-medium">{client.productsOnSite}</span>
                        </div>
                        <div className="flex items-center gap-1 px-2 py-1 bg-green-50 rounded text-xs cursor-help" title="Peças que já foram vendidas">
                          <ShoppingBag className="h-3 w-3 text-green-600 flex-shrink-0" />
                          <span className="text-[#68686A]">Vendidos:</span>
                          <span className="text-gray-900 font-medium">{client.productsSold}</span>
                        </div>
                        <div className="flex items-center gap-1 px-2 py-1 bg-emerald-50 rounded text-xs cursor-help" title="Peças vendidas que já tiveram o valor repassado (pago) ao fornecedor">
                          <DollarSign className="h-3 w-3 text-emerald-600 flex-shrink-0" />
                          <span className="text-[#68686A]">Pagos:</span>
                          <span className="text-gray-900 font-medium">{client.productsPaid}</span>
                        </div>
                        <div className="flex items-center gap-1 px-2 py-1 bg-red-50 rounded text-xs cursor-help" title="Peças devolvidas (não aprovadas, defeito ou devolução do cliente final)">
                          <XCircle className="h-3 w-3 text-red-600 flex-shrink-0" />
                          <span className="text-[#68686A]">Devolvidos:</span>
                          <span className="text-gray-900 font-medium">{client.productsReturned}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Ações */}
                <div className="flex flex-col gap-2 justify-end mt-4 md:mt-0 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEditClick(client)}
                    className="text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] border-gray-300 w-full justify-center"
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewProducts(client)}
                    className="text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] border-gray-300 w-full justify-center"
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    Detalhes
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Paginação */}
      {pagination.last_page > 1 && (
        <div className="flex items-center justify-center mt-6 mb-6">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.current_page === 1}
              onClick={() => fetchClientes(pagination.current_page - 1)}
            >
              {'<'}
            </Button>
            {(() => {
              const current = pagination.current_page;
              const last = pagination.last_page;
              const pages: (number | string)[] = [];
              const delta = 2;

              for (let i = 1; i <= last; i++) {
                if (i === 1 || i === last || (i >= current - delta && i <= current + delta)) {
                  pages.push(i);
                } else if (pages[pages.length - 1] !== "...") {
                  pages.push("...");
                }
              }

              return pages.map((page, index) => (
                page === "..." ? (
                  <span key={`dots-${index}`} className="text-gray-400 px-1">...</span>
                ) : (
                  <Button
                    key={page}
                    variant={pagination.current_page === page ? "default" : "outline"}
                    size="sm"
                    className={pagination.current_page === page ? "bg-[#D4C070] hover:bg-[#D4C070] text-white" : "text-[#68686A]"}
                    onClick={() => fetchClientes(page as number)}
                  >
                    {page}
                  </Button>
                )
              ));
            })()}
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.current_page === pagination.last_page}
              onClick={() => fetchClientes(pagination.current_page + 1)}
            >
              {'>'}
            </Button>
          </div>
        </div>
      )}

      <EditarClienteDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        client={selectedClient}
        loggedInUser={user}
        onSave={() => fetchClientes(pagination.current_page)}
      />

    </main>
  );
}

export default ClientesPage;
